import React from 'react';

import * as AccordionPrimitive from '@radix-ui/react-accordion';

import cn from '../../utils/cn';
import Typography from '../Typography';

const Accordion = React.forwardRef<React.ElementRef<typeof AccordionPrimitive.Root>, React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Root>>(
  ({ className, ...props }, ref) => <AccordionPrimitive.Root ref={ref} className={cn('bg-white', className)} {...props} />
);

Accordion.displayName = 'Accordion';

const AccordionItem = React.forwardRef<React.ElementRef<typeof AccordionPrimitive.Item>, React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Item>>(
  ({ className, ...props }, ref) => <AccordionPrimitive.Item ref={ref} className={cn('box-content rounded-md border border-gray-light', className)} {...props} />
);
AccordionItem.displayName = 'AccordionItem';

const AccordionTrigger = React.forwardRef<React.ElementRef<typeof AccordionPrimitive.Trigger>, React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger>>(
  ({ className, children, ...props }, ref) => (
    <AccordionPrimitive.Header className='flex p-5 hover:bg-gray-lightest/50'>
      <AccordionPrimitive.Trigger ref={ref} className={cn('flex flex-1 justify-between transition-all [&[data-state=open]>svg]:rotate-180', className)} {...props}>
        {children}
        <svg className='ml-5 shrink-0 text-gray-dark transition-transform duration-200' xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 16 16' fill='none'>
          <path
            fillRule='evenodd'
            clipRule='evenodd'
            d='M7.99988 11.8014L14.9082 5.63226L13.7368 4.19843L7.99995 9.32337L2.26311 4.19843L1.09174 5.63226L7.99988 11.8014Z'
            fill='#9DA9B6'
          />
        </svg>
      </AccordionPrimitive.Trigger>
    </AccordionPrimitive.Header>
  )
);
AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName;

const AccordionContent = React.forwardRef<React.ElementRef<typeof AccordionPrimitive.Content>, React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Content>>(
  ({ className, children, ...props }, ref) => (
    <AccordionPrimitive.Content
      ref={ref}
      className={cn('overflow-hidden border-t border-gray-light transition-all data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down', className)}
      {...props}
    >
      <div className='p-5'>{children}</div>
    </AccordionPrimitive.Content>
  )
);

// custom header component for accordion - design system
interface AccordionHeaderProps {
  description?: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
}

const AccordionHeader = ({ description, icon, children }: AccordionHeaderProps) => {
  return (
    <div className='flex w-full  flex-col'>
      <div className='flex items-center'>
        {icon}
        <div>{children}</div>
      </div>

      {description ? (
        <div className='flex'>
          <Typography variant='caption-default' className='mt-2.5 text-gray-dark'>
            {description}
          </Typography>
        </div>
      ) : null}
    </div>
  );
};

AccordionContent.displayName = AccordionPrimitive.Content.displayName;

export { AccordionItem, AccordionHeader, AccordionTrigger, AccordionContent };
export default Accordion;
